import React, { useState, useEffect, useCallback } from "react";
import Axios from "axios";
import { ALL_PONTO, ALL_PONTO_COLABORADOR, ALL_COLABORADORES, ALL_FERIADOS, ALL_HORARIOS } from "../../Services/Endpoints";
import { IoMdSearch, IoMdAlert } from "react-icons/io";
import { FiLock, FiUnlock, FiLogOut } from "react-icons/fi";
import { HiOutlineDocumentDownload } from 'react-icons/hi';
import jsPDF from "jspdf";
import "jspdf-autotable";
import CalendarWidget from "../CalendarWidget";
import ModalDetalhesDia from "../ModalDetalhesDia";
import ModalListaStatusDia from "../ModalListaStatusDia";
import ModalPendencias from "../ModalPendencias";
import ModalPesquisa from "../ModalPesquisa";
import ModalCrud from "../ModalCrud";
import MainContext from "../../Contexts/MainContext";
import { showToast } from "../../Functions";
import ReactGridLayout, { WidthProvider, Layout } from "react-grid-layout";
import "react-grid-layout/css/styles.css";
import "react-resizable/css/styles.css";
import "./styles.scss";

const GridLayout = WidthProvider(ReactGridLayout);

interface AdminRenderProps {
    token: string | null;
}

// Helper: check if two dates are same month/year
function isSameMonthYear(d1: Date, d2: Date): boolean {
    return d1.getFullYear() === d2.getFullYear() && d1.getMonth() === d2.getMonth();
}

// Helper: get first day of month
function getStartOfMonth(d: Date): Date {
    return new Date(d.getFullYear(), d.getMonth(), 1);
}

// Helper: Parse HH:mm to minutes
function timeToMinutes(timeStr: string): number {
    const [h, m] = timeStr.split(':').map(Number);
    return (h || 0) * 60 + (m || 0);
}

const LAYOUT_STORAGE_KEY = "marcaponto_dashboard_layout";

const DEFAULT_LAYOUT: Layout[] = [
    { i: "selector",  x: 0, y: 0, w: 6,  h: 3, minW: 4, maxW: 8,  minH: 2, maxH: 5 },
    { i: "eventos",   x: 6, y: 0, w: 6,  h: 3, minW: 4, maxW: 8,  minH: 2, maxH: 5 },
    { i: "calendario",x: 0, y: 3, w: 12, h: 7, minW: 6, maxW: 12, minH: 5, maxH: 14 },
    { i: "auditoria", x: 0, y: 10,w: 12, h: 6, minW: 6, maxW: 12, minH: 4, maxH: 10 },
];

function loadLayout(): Layout[] {
    try {
        const saved = localStorage.getItem(LAYOUT_STORAGE_KEY);
        if (saved) {
            const parsed = JSON.parse(saved);
            if (Array.isArray(parsed) && parsed.length === DEFAULT_LAYOUT.length) {
                // Merge saved positions with min/max constraints from defaults
                return parsed.map((item: Layout) => {
                    const def = DEFAULT_LAYOUT.find(d => d.i === item.i);
                    if (def) {
                        return { ...item, minW: def.minW, maxW: def.maxW, minH: def.minH, maxH: def.maxH };
                    }
                    return item;
                });
            }
        }
    } catch (e) {
        console.warn("Erro ao carregar layout salvo:", e);
    }
    return DEFAULT_LAYOUT;
}

const AdminRender: React.FC<AdminRenderProps> = ({ token }) => {
    const { removeToken } = React.useContext(MainContext);
    const [pontos, setPontos] = useState<any[]>([]);
    const [colaboradores, setColaboradores] = useState<any[]>([]);
    const [feriados, setFeriados] = useState<any[]>([]);
    const [horarios, setHorarios] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [selectedMonth, setSelectedMonth] = useState<Date>(getStartOfMonth(new Date()));
    
    // Filtros Operacionais
    const [selectedColaboradorId, setSelectedColaboradorId] = useState<number | null>(null);
    const [showOnlyPending, setShowOnlyPending] = useState<boolean>(false);

    // Modal de Edição (Auditoria)
    const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
    const [selectedDateForModal, setSelectedDateForModal] = useState<Date | null>(null);
    const [selectedColabForModal, setSelectedColabForModal] = useState<number | null>(null);

    // Modal de Status Diário (Visão Global)
    const [isStatusListModalOpen, setIsStatusListModalOpen] = useState(false);
    const [selectedDateForStatus, setSelectedDateForStatus] = useState<Date | null>(null);

    // Tempo Real
    const [currentTime, setCurrentTime] = useState<Date>(new Date());
    
    // Paginação da Tabela de Auditoria
    const [currentPage, setCurrentPage] = useState<number>(1);
    const ITEMS_PER_PAGE = 10;

    // Novos Modais
    const [isPesquisaModalOpen, setIsPesquisaModalOpen] = useState(false);
    const [isPendenciasModalOpen, setIsPendenciasModalOpen] = useState(false);
    const [isLogoutModalOpen, setIsLogoutModalOpen] = useState(false);

    // Bulk Absence Modal
    const [isBulkModalOpen, setIsBulkModalOpen] = useState(false);
    const [bulkTipo, setBulkTipo] = useState("Férias");
    const [bulkDataInicio, setBulkDataInicio] = useState("");
    const [bulkDataFim, setBulkDataFim] = useState("");
    const [bulkSaving, setBulkSaving] = useState(false);

    // Grid Layout State
    const [gridLayout, setGridLayout] = useState<Layout[]>(loadLayout());
    const [isLayoutLocked, setIsLayoutLocked] = useState(true);

    const handleLayoutChange = useCallback((newLayout: Layout[]) => {
        setGridLayout(newLayout);
        try {
            localStorage.setItem(LAYOUT_STORAGE_KEY, JSON.stringify(newLayout));
        } catch (e) {
            console.warn("Erro ao salvar layout:", e);
        }
    }, []);

    const resetLayout = useCallback(() => {
        setGridLayout(DEFAULT_LAYOUT);
        localStorage.removeItem(LAYOUT_STORAGE_KEY);
    }, []);

    const handleBulkSave = async () => {
        if (!selectedColaboradorId) {
            alert("Selecione um colaborador primeiro!");
            return;
        }
        if (!bulkDataInicio || !bulkDataFim) {
            alert("Preencha data de início e fim!");
            return;
        }
        setBulkSaving(true);
        try {
            const start = new Date(bulkDataInicio);
            const end = new Date(bulkDataFim);
            // Ensure time is at midnight local time to avoid timezone shifts
            start.setMinutes(start.getMinutes() + start.getTimezoneOffset());
            end.setMinutes(end.getMinutes() + end.getTimezoneOffset());

            let current = new Date(start);
            while (current <= end) {
                const dateStr = current.toLocaleDateString("pt-BR", { day: '2-digit', month: '2-digit', year: 'numeric' });
                await Axios.post("http://147.15.59.17:8080/api/v1/pontos", {
                    colaboradorId: selectedColaboradorId,
                    data: dateStr,
                    horario: "00:00",
                    tipo: bulkTipo,
                    status: "APROVADO",
                    justificativa: `Adicionado em lote: ${bulkTipo}`
                }, { headers: { Authorization: token } });
                current.setDate(current.getDate() + 1);
            }
            setIsBulkModalOpen(false);
            setBulkDataInicio("");
            setBulkDataFim("");
            fetchData(); // Refresh list
        } catch (e) {
            console.error(e);
            alert("Erro ao salvar em lote");
        } finally {
            setBulkSaving(false);
        }
    };

    const fetchData = useCallback(async () => {
        if (!token) return;
        try {
            const [colabRes, feriadosRes, horariosRes, pontosRes] = await Promise.all([
                Axios.get(ALL_COLABORADORES, { headers: { Authorization: token } }),
                Axios.get(ALL_FERIADOS, { headers: { Authorization: token } }),
                Axios.get(ALL_HORARIOS, { headers: { Authorization: token } }),
                Axios.get(ALL_PONTO, { headers: { Authorization: token } })
            ]);
            
            setColaboradores(colabRes.data || []);
            setFeriados(feriadosRes.data || []);
            setHorarios(horariosRes.data || []);
            setPontos(pontosRes.data || []);
        } catch (err) {
            console.error("Erro ao buscar dados do dashboard", err);
        } finally {
            setLoading(false);
        }
    }, [token]);

    useEffect(() => {
        setLoading(true);
        fetchData();
        // Atualização de dados macro
        const intervalData = setInterval(fetchData, 60000);
        return () => clearInterval(intervalData);
    }, [fetchData, selectedColaboradorId]);

    useEffect(() => {
        // Relógio para progressão gráfica
        const intervalTime = setInterval(() => setCurrentTime(new Date()), 60000);
        return () => clearInterval(intervalTime);
    }, []);

    // Apenas colaboradores operacionais reais
    const colaboradoresOperacionais = React.useMemo(() => {
        return colaboradores.filter(c => {
            if (!c.ativo && !c.isAtivo) return false;
            const perfis = c.perfis || [c.perfil];
            const isColab = perfis.some((p: string) => p && p.toUpperCase() === "COLABORADOR");
            const isAdmin = perfis.some((p: string) => p && p.toUpperCase() === "ADMIN");
            const isGestor = perfis.some((p: string) => p && (p.toUpperCase() === "GESTOR" || p.toUpperCase() === "RESPONSAVEL"));
            return isColab && !isAdmin && !isGestor;
        });
    }, [colaboradores]);

    // Aplicação dos Filtros
    const pontosDoMes = React.useMemo(() => {
        return pontos.filter(p => {
            if (!p.data) return false;
            const dateParts = p.data.split('/');
            if (dateParts.length === 3) {
                const dateObj = new Date(Number(dateParts[2]), Number(dateParts[1]) - 1, Number(dateParts[0]));
                return isSameMonthYear(dateObj, selectedMonth);
            }
            return false;
        });
    }, [pontos, selectedMonth]);

    const pontosFiltrados = React.useMemo(() => {
        if (selectedColaboradorId === null) return [];
        let filtrados = pontosDoMes.filter(p => Number(p.colaboradorId) === selectedColaboradorId);
        if (showOnlyPending) {
            filtrados = filtrados.filter(p => p.status === "PENDENTE");
        }
        return filtrados;
    }, [pontosDoMes, selectedColaboradorId, showOnlyPending]);

    // Pendências Virtuais (Dias úteis sem marcação) e Tolerância
    const pendenciasCount = React.useMemo(() => {
        let count = 0;
        // 1. Marcações pendentes (existentes no DB) ou atrasadas
        pontosDoMes.forEach(p => {
            if (p.status === "PENDENTE") {
                if (selectedColaboradorId === null || Number(p.colaboradorId) === selectedColaboradorId) {
                    count++;
                }
            } else if (p.status === "APROVADO" || p.status === "REPROVADO" || p.status === "REJEITADO") {
                // Se já foi avaliado, não conta como pendência
                return;
            } else {
                // Análise de Tolerância de 30 minutos
                if (p.tipo && p.tipo.toLowerCase().includes('entrada')) {
                    const m = timeToMinutes(p.horario);
                    if (m < 450 || m > 510) { 
                        if (selectedColaboradorId === null || Number(p.colaboradorId) === selectedColaboradorId) {
                            count++;
                        }
                    }
                }
            }
        });

        // 2. Pendências Virtuais (Ausência de batida em dia útil passado)
        const today = new Date();
        const diasNoMes = new Date(selectedMonth.getFullYear(), selectedMonth.getMonth() + 1, 0).getDate();
        
        colaboradoresOperacionais.forEach(c => {
            if (selectedColaboradorId !== null && Number(c.id) !== selectedColaboradorId) return;
            
            for (let dia = 1; dia <= diasNoMes; dia++) {
                const checkDate = new Date(selectedMonth.getFullYear(), selectedMonth.getMonth(), dia);
                if (checkDate >= today) break; // Não calcula para hoje ou futuro
                
                // Pula Finais de Semana (simples: 0=Dom, 6=Sab)
                if (checkDate.getDay() === 0 || checkDate.getDay() === 6) continue;
                
                // Pula Feriados
                const dateStr = checkDate.toISOString().split('T')[0];
                const monthDay = dateStr.substring(5);
                const isFeriado = feriados.some(f => f.data === dateStr || (f.recorrente && f.data && f.data.endsWith(monthDay)));
                if (isFeriado) continue;

                // Tem ponto neste dia?
                const ptDiaStr = `${String(dia).padStart(2, '0')}/${String(selectedMonth.getMonth() + 1).padStart(2, '0')}/${selectedMonth.getFullYear()}`;
                const hasPonto = pontosDoMes.some(p => Number(p.colaboradorId) === Number(c.id) && p.data === ptDiaStr);
                
                if (!hasPonto) {
                    count++;
                }
            }
        });

        return count;
    }, [pontosDoMes, selectedColaboradorId, colaboradoresOperacionais, feriados, selectedMonth]);

    // Eventos do mês (Feriados + Aniversariantes)
    const eventosMes = React.useMemo(() => {
        const eventos: { dataFormatada: string; descricao: string; dia: number }[] = [];
        
        colaboradoresOperacionais.forEach(c => {
            if (c.data_nascimento || c.dataNascimento) {
                const dataNasc = c.data_nascimento || c.dataNascimento;
                const parts = dataNasc.split('-');
                if (parts[1] && Number(parts[1]) === (selectedMonth.getMonth() + 1)) {
                    eventos.push({
                        dataFormatada: `${parts[2]}/${parts[1]}`,
                        descricao: `Aniv. ${c.nome.split(' ')[0]}`,
                        dia: Number(parts[2])
                    });
                }
            }
        });

        feriados.forEach(f => {
            if (f.data) {
                const parts = f.data.split('-');
                if (parts[1] && Number(parts[1]) === (selectedMonth.getMonth() + 1)) {
                    eventos.push({
                        dataFormatada: `${parts[2]}/${parts[1]}`,
                        descricao: f.nome,
                        dia: Number(parts[2])
                    });
                }
            }
        });

        return eventos.sort((a, b) => a.dia - b.dia);
    }, [colaboradoresOperacionais, feriados, selectedMonth]);

    // Gerar tabela de auditoria agrupada por dia e colaborador
    const auditTableData = React.useMemo(() => {
        const map: { [key: string]: any } = {};
        
        pontosFiltrados.forEach(p => {
            const key = `${p.data}-${p.colaboradorId}`;
            if (!map[key]) {
                const colab = colaboradoresOperacionais.find(c => Number(c.id) === Number(p.colaboradorId));
                map[key] = {
                    key,
                    dataStr: p.data,
                    colaboradorId: Number(p.colaboradorId),
                    colabObj: colab,
                    registros: [],
                    pendente: false
                };
            }
            map[key].registros.push(p);
            if (p.status === "PENDENTE") map[key].pendente = true;
        });

        const sorted = Object.values(map).map((row: any) => {
            let totalWorkedMinutes = 0;
            const records = [...row.registros].sort((a, b) => timeToMinutes(a.horario) - timeToMinutes(b.horario));
            for (let i = 0; i < records.length; i += 2) {
                if (records[i + 1]) {
                    totalWorkedMinutes += (timeToMinutes(records[i + 1].horario) - timeToMinutes(records[i].horario));
                }
            }

            const isHoliday = feriados.some(f => f.data === row.dataStr);
            let expectedMinutes = 0;

            if (!isHoliday && row.colabObj) {
                const horario = horarios.find(h => h.id === row.colabObj.horarioId) || horarios.find(h => h.padrao);
                if (horario) {
                    const parts = row.dataStr.split('/');
                    const dateObj = new Date(Number(parts[2]), Number(parts[1]) - 1, Number(parts[0]));
                    const daysArr = ['DOMINGO', 'SEGUNDA', 'TERCA', 'QUARTA', 'QUINTA', 'SEXTA', 'SABADO'];
                    const dayOfWeekStr = daysArr[dateObj.getDay()];
                    const diaRegra = horario.dias?.find((dRule: any) => dRule.diaDaSemana === dayOfWeekStr);
                    if (diaRegra && diaRegra.ativo) {
                        const inM = timeToMinutes(diaRegra.entrada);
                        const outM = timeToMinutes(diaRegra.saida);
                        const breakIn = timeToMinutes(diaRegra.inicioIntervalo);
                        const breakOut = timeToMinutes(diaRegra.fimIntervalo);
                        const workedExpected = Math.max(0, (outM - inM) - Math.max(0, breakOut - breakIn));
                        if (workedExpected > 0) expectedMinutes = workedExpected;
                    }
                }
            }

            let extra = 0;
            if (isHoliday) {
                extra = totalWorkedMinutes;
            } else {
                extra = Math.max(0, totalWorkedMinutes - expectedMinutes);
            }

            let extraStr = "00:00";
            if (extra > 0) {
                const h = Math.floor(extra / 60);
                const m = extra % 60;
                extraStr = `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`;
            }

            return {
                ...row,
                horasExtras: extraStr
            };
        }).sort((a, b) => {
            const da = a.dataStr.split('/').reverse().join('');
            const db = b.dataStr.split('/').reverse().join('');
            return db.localeCompare(da);
        });
        
        return sorted;
    }, [pontosFiltrados, colaboradoresOperacionais, feriados, horarios]);

    // Lógica de paginação
    const totalPages = Math.ceil(auditTableData.length / ITEMS_PER_PAGE);
    const paginatedAuditData = React.useMemo(() => {
        const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
        return auditTableData.slice(startIndex, startIndex + ITEMS_PER_PAGE);
    }, [auditTableData, currentPage]);

    // Gerar opções de meses
    const monthOptions: { value: string; label: string }[] = [];
    for (let i = 0; i < 6; i++) {
        const d = new Date();
        d.setMonth(d.getMonth() - i);
        const monthStart = getStartOfMonth(d);
        const label = i === 0 ? "Mês Atual" : monthStart.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' });
        monthOptions.push({ value: monthStart.toISOString(), label });
    }

    const handleExportPdf = () => {
        const doc = new jsPDF();
        const tableColumn = ["Data", "Registros", "H. Extra", "Status"];
        const tableRows: any[] = [];

        auditTableData.forEach((row: any) => {
            const dataFinal = row.pendente ? row.dataStr : `${row.dataStr}*`;
            const rowData = [
                dataFinal,
                row.registros.map((r: any) => r.horario).join(' - '),
                row.horasExtras,
                row.pendente ? 'Pendente' : 'Aprovado'
            ];
            tableRows.push(rowData);
        });

        // Cabecalho
        doc.setFontSize(16);
        doc.text("Auditoria Operacional", 14, 15);
        
        doc.setFontSize(10);
        const dataAtual = new Date().toLocaleDateString('pt-BR');
        doc.text(`Gerado em: ${dataAtual}`, 14, 22);

        // Resumo de filtros
        let filtroTxt = `Mês Referência: ${selectedMonth.toLocaleDateString('pt-BR', {month: 'long', year: 'numeric'})}`;
        if (selectedColaboradorId !== null) {
            const cNome = colaboradoresOperacionais.find(c => Number(c.id) === selectedColaboradorId)?.nome || selectedColaboradorId;
            filtroTxt += ` | Colaborador: ${cNome}`;
        }
        if (showOnlyPending) {
            filtroTxt += ` | Filtro: Apenas Pendências`;
        }
        doc.text(filtroTxt, 14, 28);

        (doc as any).autoTable({
            startY: 33,
            head: [tableColumn],
            body: tableRows,
            theme: 'striped',
            headStyles: { fillColor: [88, 80, 236] }, // Cor roxa base do sistema
            styles: { fontSize: 9 }
        });

        // Rodape com o total
        const finalY = (doc as any).lastAutoTable.finalY || 33;
        doc.text(`Total de Registros: ${auditTableData.length}`, 14, finalY + 10);

        doc.save("auditoria_operacional.pdf");
    };

    const handleCalendarDayClick = (date: Date) => {
        if (selectedColaboradorId !== null) {
            // Se já tem um colaborador selecionado, pula direto para o Modal de Detalhes dele
            setSelectedDateForModal(date);
            setSelectedColabForModal(selectedColaboradorId);
            setIsDetailsModalOpen(true);
        } else {
            // Visão Global: Abre a lista de status de todos os colaboradores naquele dia
            setSelectedDateForStatus(date);
            setIsStatusListModalOpen(true);
        }
    };

    if (loading) {
        return <div className="admin-dashboard-loading">Carregando painel dinâmico...</div>;
    }

    return (
        <div className="admin-dashboard-container">
            <div className="dashboard-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                    <h2>Auditoria Operacional RH</h2>
                </div>
                <div className="card-actions no-drag">
                    <button
                        className={`action-btn layout-toggle ${!isLayoutLocked ? 'active' : ''}`}
                        onClick={() => setIsLayoutLocked(!isLayoutLocked)}
                        title={isLayoutLocked ? "Bloqueado para edição" : "Modo de edição ativado"}
                    >
                        {isLayoutLocked ? <FiLock size={18} /> : <FiUnlock size={18} />}
                    </button>
                    {!isLayoutLocked && (
                        <button 
                            className="action-btn action-reset"
                            onClick={resetLayout}
                            title="Restaurar layout padrão"
                        >
                            <span>Resetar Grid</span>
                        </button>
                    )}
                    <button 
                        className="action-btn action-search" 
                        onClick={() => setIsPesquisaModalOpen(true)}
                        title="Pesquisa Avançada (Relatórios)"
                        style={{ padding: '8px 12px' }}
                    >
                        <IoMdSearch size={22} />
                    </button>
                    <button 
                        className="action-btn action-pendencies" 
                        onClick={() => setIsPendenciasModalOpen(true)}
                        title="Central de Pendências"
                        style={{ padding: '8px 12px' }}
                    >
                        <IoMdAlert size={22} />
                        {pendenciasCount > 0 && <span className="badge-count">{pendenciasCount}</span>}
                    </button>
                    <button 
                        className="action-btn action-logout" 
                        onClick={() => setIsLogoutModalOpen(true)}
                        title="Sair do Sistema"
                    >
                        <FiLogOut size={20} />
                        <span>Sair</span>
                    </button>
                </div>
            </div>

            {!isLayoutLocked && (
                <div className="layout-edit-banner">
                    <span>🔧 Modo de edição ativo — arraste e redimensione os cards livremente</span>
                </div>
            )}

            <GridLayout
                className={`dashboard-grid ${!isLayoutLocked ? 'editing' : ''}`}
                layout={gridLayout}
                cols={12}
                rowHeight={40}
                margin={[15, 15]}
                containerPadding={[0, 0]}
                isDraggable={!isLayoutLocked}
                isResizable={!isLayoutLocked}
                draggableCancel=".no-drag"
                onLayoutChange={handleLayoutChange}
                useCSSTransforms={true}
                compactType="vertical"
            >
                {/* Card: Seletor de Colaborador */}
                <div key="selector" className="grid-card">
                    <div className="grid-card-inner">
                        <div className="card filter-card">
                            <div className="card-info no-drag" style={{ marginLeft: 0, width: '100%' }}>
                                <select 
                                    className={`colab-selector ${selectedColaboradorId === null ? 'is-placeholder' : ''}`}
                                    value={selectedColaboradorId === null ? "all" : selectedColaboradorId}
                                    onChange={(e) => {
                                        const val = e.target.value;
                                        setSelectedColaboradorId(val === "all" ? null : Number(val));
                                        setCurrentPage(1);
                                    }}
                                >
                                    {selectedColaboradorId === null && <option value="all" hidden>Escolha o colaborador</option>}
                                    <option value="all" disabled>Selecione para exibir os dados</option>
                                    {colaboradoresOperacionais.map(c => (
                                        <option key={c.id} value={c.id}>{c.nome}</option>
                                    ))}
                                </select>
                                {selectedColaboradorId !== null && (
                                    <button 
                                        onClick={() => setIsBulkModalOpen(true)} 
                                        className="bulk-btn"
                                    >
                                        + Ausência em Lote
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Card: Eventos do Mês */}
                <div key="eventos" className="grid-card">
                    <div className="grid-card-inner">
                        <div className="card">
                            <div className="card-info" style={{ marginLeft: 0 }}>
                                <span>Eventos</span>
                                {eventosMes.length > 0 ? (
                                    <ul className="niver-list compact-list">
                                        {eventosMes.map((ev: any, idx: number) => (
                                            <li key={idx}>{ev.dataFormatada} - {ev.descricao}</li>
                                        ))}
                                    </ul>
                                ) : (
                                    <span className="empty-events">Nenhum evento este mês</span>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Card: Calendário */}
                <div key="calendario" className="grid-card">
                    <div className="grid-card-inner calendar-grid-card">
                        <h3 className="grid-card-title no-drag">Visualização Mensal</h3>
                        <div className="calendar-scroll-area no-drag">
                            <CalendarWidget 
                                pontos={pontosFiltrados} 
                                onRefresh={fetchData} 
                                onDayClick={handleCalendarDayClick} 
                                currentMonth={selectedMonth}
                                onMonthChange={setSelectedMonth}
                            />
                        </div>
                    </div>
                </div>

                {/* Card: Tabela de Auditoria */}
                <div key="auditoria" className="grid-card">
                    <div className="grid-card-inner audit-grid-card">
                        <div className="grid-card-header no-drag" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                            <h3 className="grid-card-title" style={{ margin: 0, borderBottom: 'none', paddingBottom: 0 }}>
                                Tabela de Auditoria Operacional
                                {showOnlyPending && <span className="badge-warning" style={{ marginLeft: '10px' }}>Exibindo Pendências</span>}
                            </h3>
                            <button 
                                onClick={handleExportPdf}
                                className="export-pdf-btn"
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '6px',
                                    padding: '6px 12px',
                                    backgroundColor: '#fff',
                                    color: '#475569',
                                    border: '1px solid #cbd5e1',
                                    borderRadius: '6px',
                                    fontSize: '0.85rem',
                                    fontWeight: 500,
                                    cursor: 'pointer',
                                    transition: 'all 0.2s'
                                }}
                            >
                                <HiOutlineDocumentDownload size={18} />
                                Exportar PDF
                            </button>
                        </div>
                        <div className="table-responsive no-drag">
                            <table className="audit-table">
                                <thead>
                                    <tr>
                                        <th>Data</th>
                                        <th>Registros</th>
                                        <th>H. Extra</th>
                                        <th>Status</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {paginatedAuditData.map((row: any) => (
                                        <tr key={row.key} onClick={() => {
                                            const dateParts = row.dataStr.split('/');
                                            setSelectedDateForModal(new Date(Number(dateParts[2]), Number(dateParts[1]) - 1, Number(dateParts[0])));
                                            setSelectedColabForModal(row.colaboradorId);
                                            setIsDetailsModalOpen(true);
                                        }}>
                                            <td>{row.pendente ? row.dataStr : `${row.dataStr}*`}</td>
                                            <td>{row.registros.map((r: any) => r.horario).join(' - ')}</td>
                                            <td>{row.horasExtras}</td>
                                            <td>
                                                {row.pendente ? (
                                                    <span className="status-badge pendente">Pendente</span>
                                                ) : (
                                                    <span className="status-badge aprovado">Aprovado</span>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                    {paginatedAuditData.length === 0 && (
                                        <tr>
                                            <td colSpan={4} className="text-center empty-msg">Nenhum registro encontrado para este filtro.</td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                        {totalPages > 1 && (
                            <div className="pagination-controls no-drag">
                                <button 
                                    disabled={currentPage === 1} 
                                    onClick={() => setCurrentPage(prev => prev - 1)}
                                >
                                    Anterior
                                </button>
                                <span>Página {currentPage} de {totalPages}</span>
                                <button 
                                    disabled={currentPage === totalPages} 
                                    onClick={() => setCurrentPage(prev => prev + 1)}
                                >
                                    Próxima
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </GridLayout>

            {/* === MODAIS (fora do grid, sem impacto no layout) === */}
            {isDetailsModalOpen && selectedDateForModal && (
                <ModalDetalhesDia 
                    date={selectedDateForModal}
                    pontos={selectedColabForModal ? pontos.filter(p => Number(p.colaboradorId) === selectedColabForModal) : pontosFiltrados}
                    onClose={() => setIsDetailsModalOpen(false)}
                    onSuccess={() => {
                        setIsDetailsModalOpen(false);
                        fetchData();
                    }}
                    readOnly={true}
                />
            )}

            {isStatusListModalOpen && selectedDateForStatus && (
                <ModalListaStatusDia 
                    date={selectedDateForStatus}
                    pontos={pontos.filter(p => p.data === selectedDateForStatus.toLocaleDateString("pt-BR", { day: '2-digit', month: '2-digit', year: 'numeric' }))}
                    colaboradores={colaboradoresOperacionais}
                    feriados={feriados}
                    onClose={() => setIsStatusListModalOpen(false)}
                    onSelectColaborador={(colabId) => {
                        setIsStatusListModalOpen(false);
                        setSelectedDateForModal(selectedDateForStatus);
                        setSelectedColabForModal(colabId);
                        setIsDetailsModalOpen(true);
                    }}
                />
            )}
            {isPendenciasModalOpen && (
                <ModalPendencias 
                    pontos={pontosDoMes}
                    colaboradores={colaboradoresOperacionais}
                    feriados={feriados}
                    selectedMonth={selectedMonth}
                    onClose={() => setIsPendenciasModalOpen(false)}
                    onResolve={(date, colabId) => {
                        setIsPendenciasModalOpen(false);
                        setSelectedDateForModal(date);
                        setSelectedColabForModal(colabId);
                        setIsDetailsModalOpen(true);
                    }}
                />
            )}

            {isLogoutModalOpen && (
                <ModalCrud onClose={() => { setIsLogoutModalOpen(false); return {}; }}>
                    <div className="logout__modal" style={{ padding: '20px', textAlign: 'center' }}>
                        <h3 style={{ marginTop: 0 }}>Sair do sistema</h3>
                        <p style={{ color: '#64748b' }}>Tem certeza que deseja encerrar sua sessão?</p>
                        <div className="logout__actions" style={{ display: 'flex', justifyContent: 'center', gap: '15px', marginTop: '20px' }}>
                            <button 
                                onClick={() => setIsLogoutModalOpen(false)}
                                style={{ padding: '10px 20px', border: '1px solid #cbd5e1', background: '#f8fafc', borderRadius: '6px', cursor: 'pointer' }}
                            >
                                Cancelar
                            </button>
                            <button 
                                onClick={() => {
                                    showToast("SUCCESS", "Você foi deslogado com sucesso", {});
                                    removeToken("token");
                                }}
                                style={{ padding: '10px 20px', background: '#ef4444', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer' }}
                            >
                                Sim, sair
                            </button>
                        </div>
                    </div>
                </ModalCrud>
            )}

            {isBulkModalOpen && (
                <div className="modal-overlay" style={{position:'fixed', top:0, left:0, width:'100%', height:'100%', backgroundColor:'rgba(0,0,0,0.6)', zIndex:999, display:'flex', alignItems:'center', justifyContent:'center', backdropFilter: 'blur(3px)'}}>
                    <div className="modal-content" style={{background:'#fff', padding:'30px', borderRadius:'12px', width: '450px', boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)', display: 'flex', flexDirection: 'column', gap: '20px'}}>
                        <div style={{borderBottom: '1px solid #e2e8f0', paddingBottom: '15px'}}>
                            <h3 style={{margin: 0, color: '#1e293b', fontSize: '1.25rem', fontWeight: 600}}>Adicionar Ausência em Lote</h3>
                            <p style={{margin: '5px 0 0 0', color: '#64748b', fontSize: '0.875rem'}}>Registre férias, folgas ou afastamentos para vários dias de uma só vez.</p>
                        </div>
                        
                        <div style={{display: 'flex', flexDirection: 'column', gap: '15px'}}>
                            <div style={{display: 'flex', flexDirection: 'column', gap: '5px'}}>
                                <label style={{color: '#475569', fontSize: '0.875rem', fontWeight: 500}}>Tipo de Registro</label>
                                <select value={bulkTipo} onChange={(e) => setBulkTipo(e.target.value)} style={{width:'100%', padding:'10px 12px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '0.95rem', color: '#334155', outline: 'none'}}>
                                    <option value="Férias">Férias</option>
                                    <option value="Folga">Folga</option>
                                    <option value="Afastamento">Afastamento</option>
                                </select>
                            </div>
                            
                            <div style={{display: 'flex', gap: '15px'}}>
                                <div style={{display: 'flex', flexDirection: 'column', gap: '5px', flex: 1}}>
                                    <label style={{color: '#475569', fontSize: '0.875rem', fontWeight: 500}}>Data de Início</label>
                                    <input type="date" value={bulkDataInicio} onChange={e => setBulkDataInicio(e.target.value)} style={{width:'100%', padding:'10px 12px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '0.95rem', color: '#334155', outline: 'none'}}/>
                                </div>
                                <div style={{display: 'flex', flexDirection: 'column', gap: '5px', flex: 1}}>
                                    <label style={{color: '#475569', fontSize: '0.875rem', fontWeight: 500}}>Data Final</label>
                                    <input type="date" value={bulkDataFim} onChange={e => setBulkDataFim(e.target.value)} style={{width:'100%', padding:'10px 12px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '0.95rem', color: '#334155', outline: 'none'}}/>
                                </div>
                            </div>
                        </div>
                            
                        <div style={{display:'flex', justifyContent:'flex-end', gap:'12px', marginTop: '10px', paddingTop: '15px', borderTop: '1px solid #e2e8f0'}}>
                            <button onClick={() => setIsBulkModalOpen(false)} style={{padding:'10px 18px', border:'1px solid #cbd5e1', background:'#f8fafc', color: '#475569', borderRadius:'6px', cursor:'pointer', fontWeight: 500, transition: 'background 0.2s'}}>Cancelar</button>
                            <button onClick={handleBulkSave} disabled={bulkSaving} style={{padding:'10px 18px', background:'#5850EC', color:'#fff', border:'none', borderRadius:'6px', cursor:'pointer', fontWeight: 500, transition: 'background 0.2s', opacity: bulkSaving ? 0.7 : 1}}>
                                {bulkSaving ? "Salvando registros..." : "Aplicar em Lote"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
            {isPesquisaModalOpen && (
                <ModalPesquisa 
                    pontos={pontos}
                    colaboradores={colaboradoresOperacionais}
                    onClose={() => setIsPesquisaModalOpen(false)}
                />
            )}
        </div>
    );
};

export default AdminRender;
